import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
const caseStudies: Record<string, {
  company: string;
  industry: string;
  title: string;
  image: string;
  results: string[];
  challenge: string[];
  solution: string[];
  outcome: string[];
  quote?: {
    text: string;
    author: string;
    role: string;
  };
}> = {
  hextronics: {
    company: 'Hextronics',
    industry: 'Manufacturing',
    title: 'How Hextronics replaced manual production tracking with real-time visibility',
    image: 'https://images.unsplash.com/photo-1473968512647-3e447244af8f?auto=format&fit=crop&q=80&w=1200&h=600',
    results: ['Single source of truth', 'Full inventory tracking', 'Iterative system build'],
    challenge: ['Hextronics manufactures autonomous drone docking stations — complex electromechanical assemblies with hundreds of components and strict quality requirements. Their production team was tracking everything in spreadsheets and whiteboards.', 'They tried both Cin7 and Microsoft Dynamics to track their manufacturing inventory, but both systems were unable to be used in any way by the Hextronics team. The complexity and rigidity of these legacy systems made them impractical for their fast-moving drone manufacturing operation.', 'They needed a production system that could keep up with their engineering pace and actually work for their team — not a legacy system that would force them to change how they work.'],
    solution: ['Hextronics started with purchasing, then upgraded to inventory management, and now handle manufacturing as well as accounting. Hextronics has migrated off of Cin7, QuickBooks, and HubSpot into a single unified system.', 'Thruline then expanded adjusted their purchasing workflows, inventory tracking, and manufacturing processes to get them using comprehensive inventory management. They now track all of their production runs, bills of materials, and components within Thruline.', 'We also implemented sales orders and quoting functionality, giving them end-to-end visibility from quote to cash. Every configuration was built iteratively based on their actual workflows and requirements.'],
    outcome: ['Thruline were able to iteratively build a system specifically for Hextronics. They can now do inventory management, manufacturing tracking, and purchase requisition with their small team, all tracked within Thruline.', 'Hextronics now has a single source of truth for inventory, sales, quoting, as well as manufacturing. No more jumping between multiple systems or reconciling data across platforms.', 'The system grows with their business — as their needs evolve, Thruline continue to build new capabilities that match their exact requirements.'],
    quote: {
      text: 'Thruline Rocks!',
      author: 'Shaun',
      role: 'Operations Manager'
    }
  },
  'xbow-systems': {
    company: 'XBow Systems',
    industry: 'Defense Manufacturing',
    title: 'Building a GovCloud-ready ERP for defense manufacturing',
    image: 'https://images.unsplash.com/photo-1517976487492-5750f3195933?auto=format&fit=crop&q=80&w=1200&h=600',
    results: ['GovCloud compliant', 'Full traceability', 'Multi-site production'],
    challenge: ['XBow Systems manufactures solid rocket motors for defense and space applications. As a government contractor, they face strict compliance requirements including ITAR and the need for GovCloud-hosted infrastructure.', "Their existing tools — a mix of spreadsheets, shared drives, and a basic inventory system — couldn't provide the traceability, access controls or inventory tracking required for their complex formulations and manufacturing.", 'They evaluated traditional defense ERP systems but found them prohibitively expensive, slow to implement, and designed for companies ten times their size.'],
    solution: ["XBow started with Procurement and Inventory, building the foundation for their operations. We've now expanded to include integrations with SAM vendor watch list functionality for compliance tracking.", 'We built out production planning as well as manufacturing across three different sites. This includes custom approval flows for purchasing and the RFQ process, ensuring proper oversight and compliance at every step.', 'We also implemented custom formulation-based manufacturing tracking for their R&D and full manufacturing teams, handling the unique requirements of energetic material processing and propellant batch tracking.'],
    outcome: ['XBow now has a fully compliant ERP system that satisfies government requirements and keeps their engineering team productive across all their manufacturing sites.', 'Full forward and backward traceability is maintained from raw material certificates through final acceptance testing. Every configuration in the system is linked to a documented requirement.', 'The system runs on GovCloud with the security controls their contracts require, without the million-dollar price tag of traditional defense ERP systems.'],
    quote: {
      text: 'Thruline helps us operate in startup mode by giving us a system that isnt overly complex',
      author: 'VP of Operations',
      role: 'XBow Systems'
    }
  },
  fredsense: {
    company: 'Fredsense',
    industry: 'Biotech',
    title: 'Building a custom lab testing and production suite for biotech R&D',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80&w=1200&h=600',
    results: ['Faster invoice tracking', 'Unified customer reports', 'Custom white-labeled ERP'],
    challenge: ['Fredsense needed a completely custom lab testing, tracking, and manufacturing production suite, as well as an invoice management tool for their biotech R&D lab testing and production kits.', 'Their spreadsheet-based system for managing tests was becoming unmanageable as they scaled. Test data, sample tracking, and customer reporting were scattered across multiple files with no unified view.', 'They needed a system that could handle their unique biotech workflows — from contract management to sample testing to production kits — without forcing them into a generic lab information management system.'],
    solution: ['Fredsense has a completely custom white-labeled ERP system that tracks everything from their contracts to sample tests, sample orders, as well as inventory. The system was built specifically for their biotech workflows.', "Thruline was able to construct this within the 60-day timeline that meets all of Fredsense's custom requirements. The system handles their unique testing protocols, sample tracking, and production workflows.", 'We built custom modules for lab test management, sample order processing, and inventory tracking — all integrated into a single platform that replaces their spreadsheet-based approach.'],
    outcome: ['Invoice tracking and analysis is now significantly faster for Fredsense. What used to take hours of manual work is now automated and accurate.', 'They now have all reports for their customers in one place, making it easy to generate compliance documentation and test results. Customer-facing reports are generated automatically from test data.', 'We are now looking to expand the system to better track growing inventory with growing sales, as well as installing our sales and quoting functionality to support their continued growth.'],
    quote: {
      text: 'Thruline built us a completely custom system that handles our unique biotech workflows. Everything from sample tracking to customer reports is now in one place.',
      author: 'Operations Manager',
      role: 'Fredsense'
    }
  }
};
export function CaseStudyPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const study = slug ? caseStudies[slug] : null;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!study) {
    return <div className="min-h-screen bg-navy text-white pt-32 pb-20 flex items-center justify-center" data-id="element-765">
        <div className="text-center" data-id="element-766">
          <h1 className="text-3xl font-bold mb-4" data-id="element-767">Case study not found</h1>
          <Link to="/resources/case-studies" className="text-violet-electric hover:underline" data-id="element-768">

            Back to case studies
          </Link>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-navy text-white pt-32 pb-20" data-id="element-769">
      <article className="max-w-4xl mx-auto px-6" data-id="element-770">
        {/* Back link */}
        <motion.div initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-10" data-id="element-771">

          <Link to="/resources/case-studies" className="inline-flex items-center gap-2 text-white/40 text-sm hover:text-white transition-colors" data-id="element-772">

            <ArrowLeftIcon className="w-4 h-4" data-id="element-773" />
            Back to case studies
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="mb-10" data-id="element-774">

          <div className="flex items-center gap-3 mb-4" data-id="element-775">
            <span className="text-violet-electric text-xs font-semibold tracking-widest uppercase" data-id="element-776">
              {study.industry}
            </span>
            <span className="text-white/20" data-id="element-777">•</span>
            <span className="text-white/40 text-xs" data-id="element-778">{study.company}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-6" data-id="element-779">
            {study.title}
          </h1>
          <div className="flex flex-wrap gap-3" data-id="element-780">
            {study.results.map(result => <span key={result} className="px-4 py-2 rounded-full bg-violet-electric/10 text-violet-electric text-sm font-medium" data-id="element-781">

                {result}
              </span>)}
          </div>
        </motion.div>

        {/* Hero Image */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="rounded-2xl overflow-hidden mb-16 border border-white/[0.06]" data-id="element-782">

          <img src={study.image} alt={study.company} className="w-full h-64 sm:h-80 md:h-96 object-cover" data-id="element-783" />

        </motion.div>

        {/* Challenge */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-16" data-id="element-784">

          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-785">
            <span className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center text-red-400 text-sm font-bold" data-id="element-786">
              1
            </span>
            The Challenge
          </h2>
          <div className="space-y-4 pl-11" data-id="element-787">
            {study.challenge.map((p, i) => <p key={i} className="text-white/60 text-lg leading-relaxed" data-id="element-788">
                {p}
              </p>)}
          </div>
        </motion.section>

        {/* Solution */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-789">

          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-790">
            <span className="w-8 h-8 rounded-lg bg-violet-electric/15 flex items-center justify-center text-violet-electric text-sm font-bold" data-id="element-791">
              2
            </span>
            The Solution
          </h2>
          <div className="space-y-4 pl-11" data-id="element-792">
            {study.solution.map((p, i) => <p key={i} className="text-white/60 text-lg leading-relaxed" data-id="element-793">
                {p}
              </p>)}
          </div>
        </motion.section>

        {/* Outcome */}
        <motion.section initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-794">

          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-795">
            <span className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center text-emerald-400 text-sm font-bold" data-id="element-796">
              3
            </span>
            The Outcome
          </h2>
          <div className="space-y-4 pl-11" data-id="element-797">
            {study.outcome.map((p, i) => <p key={i} className="text-white/60 text-lg leading-relaxed" data-id="element-798">
                {p}
              </p>)}
          </div>
        </motion.section>

        {/* Quote */}
        {study.quote && <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="rounded-2xl border border-violet-electric/20 bg-violet-electric/[0.04] p-10 mb-16 relative overflow-hidden" data-id="element-799">

            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-violet-electric to-magenta-hot" data-id="element-800" />
            <p className="text-xl md:text-2xl text-white/80 font-light leading-relaxed mb-6 italic" data-id="element-801">
              "{study.quote.text}"
            </p>
            <div data-id="element-802">
              <p className="text-white font-semibold" data-id="element-803">{study.quote.author}</p>
              <p className="text-white/40 text-sm" data-id="element-804">{study.quote.role}</p>
            </div>
          </motion.div>}

        {/* Results Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-10 mb-16" data-id="element-805">

          <h3 className="text-xl font-bold text-white mb-6" data-id="element-806">Key Results</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-807">
            {study.results.map(result => <div key={result} className="flex items-center gap-3" data-id="element-808">
                <div className="w-6 h-6 rounded-full bg-emerald-400/20 flex items-center justify-center flex-shrink-0" data-id="element-809">
                  <CheckIcon className="w-3.5 h-3.5 text-emerald-400" data-id="element-810" />
                </div>
                <span className="text-white/70 text-sm font-medium" data-id="element-811">
                  {result}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center" data-id="element-812">

          <h3 className="text-2xl font-bold text-white mb-3" data-id="element-813">
            Ready to see similar results?
          </h3>
          <p className="text-white/50 mb-8" data-id="element-814">
            Discovery is free. Share your documents and we'll build a project
            plan.
          </p>
          <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-815">

            Start Free Discovery
            <ArrowRightIcon className="w-4 h-4" data-id="element-816" />
          </a>
        </motion.div>
      </article>
    </div>;
}